<script setup lang="ts">
import { ref, computed, useSlots } from "vue";
import { useRoute } from "vue-router";
import IconManager from "@/core/IconManager.vue";
import type { IconName } from "@/core/IconManager.vue";

const props = defineProps<{
  to?: string;
  icon: IconName;
  label: string;
}>();

const emit = defineEmits<{ navigate: [] }>();

const route = useRoute();
const slots = useSlots();
const isOpen = ref(false);

const hasSubOptions = computed(() => !!slots.default);

const isActive = computed(() => !!props.to && route.path === props.to);

function toggleOpen() {
  isOpen.value = !isOpen.value;
}
</script>

<template>
  <!-- Leaf item: plain navigable link -->
  <router-link
    v-if="!hasSubOptions"
    :to="to!"
    @click="emit('navigate')"
    class="flex flex-row items-center gap-2 px-3 py-2 sm:p-3 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 text-gray-700"
    :class="
      isActive
        ? 'bg-gradient-to-r from-primary to-primary text-white shadow-lg shadow-blue-500/25 scale-[1.02]'
        : 'hover:bg-gray-100 hover:text-gray-900 hover:shadow-md hover:scale-[1.01]'
    "
  >
    <IconManager
      :name="icon"
      class="w-4 h-4 sm:w-5 sm:h-5 shrink-0 transition-transform duration-200"
      :class="isActive ? 'text-white scale-110' : 'text-gray-500'"
    />
    <span class="font-semibold truncate">{{ label }}</span>
  </router-link>

  <!-- Group item: expandable, content provided via slot -->
  <div v-else class="flex flex-col">
    <button
      type="button"
      @click="toggleOpen"
      class="flex flex-row items-center gap-2 px-3 py-2 sm:p-3 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 text-gray-700 w-full"
      :class="
        isOpen
          ? 'bg-gray-100 text-gray-900'
          : 'hover:bg-gray-100 hover:text-gray-900 hover:shadow-md'
      "
    >
      <IconManager
        :name="icon"
        class="w-4 h-4 sm:w-5 sm:h-5 shrink-0 text-gray-500"
      />
      <span class="font-semibold truncate flex-1 text-left">{{ label }}</span>
      <IconManager
        name="ArrowDown"
        class="w-4 h-4 shrink-0 transition-transform duration-200 text-gray-500"
        :class="isOpen ? 'rotate-0' : '-rotate-90'"
      />
    </button>

    <div
      v-if="isOpen"
      class="flex flex-col gap-1 mt-1 pl-6 border-l border-gray-200 ml-4"
    >
      <slot></slot>
    </div>
  </div>
</template>
